import { usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Printer, ArrowLeft } from "lucide-react"
import { assetTypeLabel } from "@/lib/assetTypes"
import { formatCNPJ, formatPhone } from "@/lib/masks"
import type { AssetType } from "@/lib/assetTypes"

interface OrderAccount {
  name: string
  tax_name: string | null
  tax_id: string | null
  phone: string | null
  street: string | null
  city: string | null
  state: string | null
  logo_url: string | null
}

interface OrderCustomer {
  id: string
  name: string
  tax_name: string | null
  tax_id: string | null
  email: string | null
  phone: string | null
  street: string | null
  city: string | null
  state: string | null
}

interface OrderAsset {
  id: string
  code: string
  assetable_type: AssetType
  product?: { id: string; name: string } | null
  volume?: number | string | null
}

interface OrderPreviewPageProps {
  account: OrderAccount
  customer: OrderCustomer
  assets: OrderAsset[]
  generatedAt: string
}

const MIN_PRODUCT_ROWS = 10

export default function OrderPreview() {
  const { t, i18n } = useTranslation()
  const { account, customer, assets, generatedAt } = usePage<OrderPreviewPageProps>().props

  const date = new Date(generatedAt)
  const formattedDate = date.toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })

  // Kegs go in the product table (one row per keg); everything else is equipment.
  const kegs = assets.filter((a) => a.assetable_type === "Keg")
  const equipmentAssets = assets.filter((a) => a.assetable_type !== "Keg")
  const equipmentByType = equipmentAssets.reduce<Record<string, number>>((acc, asset) => {
    acc[asset.assetable_type] = (acc[asset.assetable_type] || 0) + 1
    return acc
  }, {})

  const productPadRows = Math.max(MIN_PRODUCT_ROWS - kegs.length, 0)

  const accountAddress = [account.street, account.city, account.state].filter(Boolean).join(", ")

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      {/* Toolbar - hidden when printing */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("order.preview.back")}
        </Button>
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="w-4 h-4 mr-2" />
          {t("order.preview.print")}
        </Button>
      </div>

      {/* Order document */}
      <div className="max-w-[210mm] mx-auto bg-white shadow-lg print:shadow-none my-8 print:my-0 p-8 print:p-6 font-sans text-[11px] leading-tight">
        {/* Company Header */}
        <div className="border border-black p-3 mb-0">
          <div className="flex items-start gap-4">
            {account.logo_url && (
              <img
                src={account.logo_url}
                alt={account.name}
                className="max-h-14 object-contain"
              />
            )}
            <div>
              <p className="font-bold text-sm">{account.tax_name || account.name}</p>
              <p>
                {account.tax_id ? `CNPJ: ${formatCNPJ(account.tax_id)}` : ""}
                {account.phone ? `  FONE: ${formatPhone(account.phone)}` : ""}
              </p>
              <p>{accountAddress}</p>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="border border-t-0 border-black py-1 text-center">
          <h1 className="text-lg font-bold tracking-wide">{t("order.document.title")}</h1>
        </div>

        {/* Order Info Section */}
        <div className="border border-t-0 border-black p-3 space-y-2">
          <div className="grid grid-cols-3 gap-x-4 gap-y-1">
            <div className="flex items-baseline gap-1">
              <span className="font-bold whitespace-nowrap">{t("order.document.customer")}:</span>
              <span className="border-b border-black flex-1 min-w-0">{customer.tax_name || customer.name}</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-bold whitespace-nowrap">{t("order.document.phone")}:</span>
              <span className="border-b border-black flex-1 min-w-0">{customer.phone ? formatPhone(customer.phone) : ""}</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-bold whitespace-nowrap">{t("order.document.orderDate")}:</span>
              <span className="border-b border-black flex-1 min-w-0">{formattedDate}</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-x-4 gap-y-1">
            <div className="flex items-baseline gap-1">
              <span className="font-bold whitespace-nowrap">{t("order.document.taxId")}:</span>
              <span className="border-b border-black flex-1 min-w-0">{customer.tax_id ? formatCNPJ(customer.tax_id) : ""}</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-bold whitespace-nowrap">{t("order.document.mobile")}:</span>
              <span className="border-b border-black flex-1 min-w-0">&nbsp;</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-bold whitespace-nowrap">{t("order.document.pickupEquipDate")}:</span>
              <span className="border-b border-black flex-1 min-w-0">____/____/____</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-x-4 gap-y-1">
            <div className="col-span-1 flex items-baseline gap-1">
              <span className="font-bold whitespace-nowrap">{t("order.document.state")}:</span>
              <span className="border-b border-black flex-1 min-w-0">{customer.state || ""}</span>
              <span className="font-bold whitespace-nowrap ml-2">{t("order.document.city")}:</span>
              <span className="border-b border-black flex-1 min-w-0">{customer.city || ""}</span>
            </div>
            <div className="col-span-2 flex items-baseline gap-1">
              <span className="font-bold whitespace-nowrap">{t("order.document.address")}:</span>
              <span className="border-b border-black flex-1 min-w-0">{customer.street || ""}</span>
            </div>
          </div>

          <div className="flex items-baseline gap-1">
            <span className="font-bold whitespace-nowrap">{t("order.document.notes")}:</span>
            <span className="border-b border-black flex-1 min-w-0">&nbsp;</span>
          </div>
        </div>

        {/* Products Table — one row per keg */}
        <table className="w-full border-collapse border border-t-0 border-black">
          <thead>
            <tr>
              <th className="border border-black p-1 text-left font-bold">{t("order.document.product")}</th>
              <th className="border border-black p-1 text-left font-bold w-24">{t("order.document.code")}</th>
              <th className="border border-black p-1 text-center font-bold w-20">{t("order.document.volume")}</th>
              <th className="border border-black p-1 text-center font-bold w-20">{t("order.document.quantity")}</th>
            </tr>
          </thead>
          <tbody>
            {kegs.map((keg) => (
              <tr key={keg.id}>
                <td className="border border-black p-1 h-6">{keg.product?.name || ""}</td>
                <td className="border border-black p-1 h-6">{keg.code}</td>
                <td className="border border-black p-1 h-6 text-center">{keg.volume ?? ""}</td>
                <td className="border border-black p-1 h-6 text-center">1</td>
              </tr>
            ))}
            {Array.from({ length: productPadRows }).map((_, i) => (
              <tr key={`pad-${i}`}>
                <td className="border border-black p-1 h-6">&nbsp;</td>
                <td className="border border-black p-1 h-6">&nbsp;</td>
                <td className="border border-black p-1 h-6">&nbsp;</td>
                <td className="border border-black p-1 h-6">&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Equipment Type Table */}
        <table className="w-full border-collapse border border-t-0 border-black">
          <thead>
            <tr>
              <th className="border border-black p-1 text-left font-bold">{t("order.document.equipmentType")}</th>
              <th className="border border-black p-1 text-center font-bold w-24">{t("order.document.quantity")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(equipmentByType).map(([type, count]) => (
              <tr key={type}>
                <td className="border border-black p-1">{assetTypeLabel(type as AssetType, t)}</td>
                <td className="border border-black p-1 text-center">{count}</td>
              </tr>
            ))}
            {/* Empty rows for manual additions */}
            {Array.from({ length: Math.max(3 - Object.keys(equipmentByType).length, 1) }).map((_, i) => (
              <tr key={`empty-${i}`}>
                <td className="border border-black p-1 h-6">&nbsp;</td>
                <td className="border border-black p-1 h-6">&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Signature Section */}
        <div className="border border-t-0 border-black p-3">
          <div className="grid grid-cols-2 gap-8 mt-6">
            <div className="text-center">
              <div className="border-t border-black pt-1">
                <p className="font-bold">{t("order.document.responsible")}</p>
                <p className="flex items-baseline gap-1 justify-center text-[10px]">
                  <span>RG/CPF:</span>
                  <span className="border-b border-black flex-1 min-w-0">&nbsp;</span>
                </p>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-black pt-1">
                <p className="font-bold">{t("order.document.customerSignature")}</p>
                <p className="flex items-baseline gap-1 justify-center text-[10px]">
                  <span>RG/CPF:</span>
                  <span className="border-b border-black flex-1 min-w-0">&nbsp;</span>
                </p>
              </div>
            </div>
          </div>
          <div className="text-center mt-4">
            <span className="font-bold">{t("order.document.date")}:</span> {formattedDate}
          </div>
        </div>
      </div>
    </div>
  )
}
